import React, { useState, useEffect, useCallback } from 'react';
import FloatingDock from '@/components/layout/FloatingDock';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Lightbulb, ThumbsUp, ThumbsDown, MessageSquare, Send,
  Filter, TrendingUp, Clock, ChevronDown, ChevronUp, Plus, X
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'general', label: 'General' },
  { id: 'features', label: 'Features' },
  { id: 'ui', label: 'UI/Design' },
  { id: 'performance', label: 'Performance' },
  { id: 'safety', label: 'Safety & Privacy' },
  { id: 'monetization', label: 'Monetization' },
  { id: 'community', label: 'Community' },
];

const STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  reviewing: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300',
  implemented: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  declined: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};

const SuggestionBox = () => {
  const [user, setUser] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState('recent');
  const [category, setCategory] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [formCategory, setFormCategory] = useState('general');
  const [submitting, setSubmitting] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [comments, setComments] = useState({});
  const [newComment, setNewComment] = useState('');

  useEffect(() => {
    const u = localStorage.getItem('socyy_user');
    if (u) setUser(JSON.parse(u));
  }, []);

  const fetchSuggestions = useCallback(async () => {
    try {
      const [sugRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/suggestions/?sort=${sort}&category=${category}&limit=50`, { withCredentials: true }),
        axios.get(`${API_URL}/suggestions/stats`, { withCredentials: true })
      ]);
      setSuggestions(sugRes.data.suggestions || []);
      setStats(statsRes.data);
    } catch (e) { /* silent */ }
    setLoading(false);
  }, [sort, category]);

  useEffect(() => { fetchSuggestions(); }, [fetchSuggestions]);

  const submitSuggestion = async () => {
    if (!title.trim() || !description.trim()) {
      toast.error('Title and description are required');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/suggestions/`, {
        title: title.trim(),
        description: description.trim(),
        category: formCategory
      }, { withCredentials: true });
      setSuggestions(prev => [res.data, ...prev]);
      setTitle('');
      setDescription('');
      setFormCategory('general');
      setShowForm(false);
      toast.success('Suggestion submitted!');
      fetchSuggestions();
    } catch (e) {
      toast.error('Failed to submit suggestion');
    }
    setSubmitting(false);
  };

  const voteSuggestion = async (sugId, vote) => {
    try {
      const res = await axios.post(`${API_URL}/suggestions/${sugId}/vote?vote=${vote}`, {}, { withCredentials: true });
      setSuggestions(prev => prev.map(s =>
        s.suggestion_id === sugId ? {
          ...s,
          upvotes: res.data.upvotes,
          downvotes: res.data.downvotes,
          user_vote: res.data.status === 'removed' ? null : res.data.status
        } : s
      ));
    } catch (e) {
      toast.error('Failed to vote');
    }
  };

  const toggleComments = async (sugId) => {
    if (expandedId === sugId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(sugId);
    if (!comments[sugId]) {
      try {
        const res = await axios.get(`${API_URL}/suggestions/${sugId}/comments`, { withCredentials: true });
        setComments(prev => ({ ...prev, [sugId]: res.data.comments || [] }));
      } catch (e) { /* silent */ }
    }
  };

  const submitComment = async (sugId) => {
    if (!newComment.trim()) return;
    try {
      const res = await axios.post(`${API_URL}/suggestions/${sugId}/comment?content=${encodeURIComponent(newComment.trim())}`, {}, { withCredentials: true });
      setComments(prev => ({ ...prev, [sugId]: [res.data, ...(prev[sugId] || [])] }));
      setSuggestions(prev => prev.map(s =>
        s.suggestion_id === sugId ? { ...s, comment_count: (s.comment_count || 0) + 1 } : s
      ));
      setNewComment('');
      toast.success('Comment added');
    } catch (e) {
      toast.error('Failed to add comment');
    }
  };

  const formatTime = (dateStr) => {
    try { return formatDistanceToNow(new Date(dateStr), { addSuffix: true }); }
    catch { return dateStr; }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black pb-24">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-violet-600 text-white">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center gap-3 mb-2">
            <Lightbulb className="w-8 h-8" />
            <h1 className="text-2xl font-bold">Suggestion Box</h1>
          </div>
          <p className="text-indigo-100 text-sm">Help shape the future of Socyy. Share your ideas and vote on what matters.</p>

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-4 gap-3 mt-5">
              {[
                { label: 'Total', value: stats.total, color: 'bg-white/20' },
                { label: 'New', value: stats.new, color: 'bg-blue-400/30' },
                { label: 'Reviewing', value: stats.reviewing, color: 'bg-yellow-400/30' },
                { label: 'Implemented', value: stats.implemented, color: 'bg-green-400/30' },
              ].map(s => (
                <div key={s.label} className={`${s.color} rounded-xl p-3 text-center`}>
                  <p className="text-xl font-bold">{s.value}</p>
                  <p className="text-xs opacity-80">{s.label}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Controls Bar */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Sort */}
            <div className="flex items-center gap-1 bg-white dark:bg-gray-800 rounded-full p-1 shadow-sm">
              {[
                { id: 'recent', label: 'New', icon: Clock },
                { id: 'top', label: 'Top', icon: TrendingUp },
              ].map(s => (
                <button
                  key={s.id}
                  onClick={() => setSort(s.id)}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                    sort === s.id ? 'bg-indigo-500 text-white' : 'text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid={`sort-${s.id}`}
                >
                  <s.icon className="w-3 h-3" /> {s.label}
                </button>
              ))}
            </div>
            {/* Category */}
            <div className="flex items-center gap-1 overflow-x-auto no-scrollbar">
              {CATEGORIES.map(c => (
                <button
                  key={c.id}
                  onClick={() => setCategory(c.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${
                    category === c.id ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                  data-testid={`category-${c.id}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
          <Button
            onClick={() => setShowForm(!showForm)}
            className="rounded-full bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-bold shadow-lg"
            data-testid="new-suggestion-btn"
          >
            {showForm ? <X className="w-4 h-4 mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
            {showForm ? 'Cancel' : 'New Suggestion'}
          </Button>
        </div>

        {/* New Suggestion Form */}
        {showForm && (
          <Card className="p-5 mb-6 rounded-2xl border-2 border-indigo-200 dark:border-indigo-800" data-testid="suggestion-form">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2 text-gray-900 dark:text-white">
              <Lightbulb className="w-5 h-5 text-indigo-500" /> Share Your Idea
            </h3>
            <div className="space-y-3">
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Give your suggestion a title..."
                className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400"
                data-testid="suggestion-title-input"
              />
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe your suggestion in detail..."
                rows={4}
                className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 resize-none"
                data-testid="suggestion-desc-input"
              />
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-gray-500">Category:</span>
                  {CATEGORIES.filter(c => c.id !== 'all').map(c => (
                    <button
                      key={c.id}
                      onClick={() => setFormCategory(c.id)}
                      className={`px-3 py-1 rounded-full text-xs font-bold transition-colors ${
                        formCategory === c.id ? 'bg-indigo-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                      }`}
                      data-testid={`form-cat-${c.id}`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
                <Button
                  onClick={submitSuggestion}
                  disabled={submitting || !title.trim() || !description.trim()}
                  className="rounded-full bg-indigo-600 hover:bg-indigo-500 text-white font-bold"
                  data-testid="submit-suggestion-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {submitting ? 'Submitting...' : 'Submit'}
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Suggestions List */}
        <div className="space-y-4" data-testid="suggestions-list">
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading suggestions...</div>
          ) : suggestions.length === 0 ? (
            <Card className="p-8 text-center rounded-2xl" data-testid="no-suggestions">
              <Lightbulb className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500 font-medium">No suggestions yet</p>
              <p className="text-gray-400 text-sm mt-1">Be the first to share an idea!</p>
            </Card>
          ) : (
            suggestions.map(sug => (
              <Card key={sug.suggestion_id} className="rounded-2xl overflow-hidden" data-testid={`suggestion-${sug.suggestion_id}`}>
                <div className="p-4">
                  <div className="flex gap-3">
                    {/* Vote Column */}
                    <div className="flex flex-col items-center gap-1 pt-1">
                      <button
                        onClick={() => voteSuggestion(sug.suggestion_id, 'up')}
                        className={`p-1.5 rounded-lg transition-all ${
                          sug.user_vote === 'up' ? 'bg-indigo-500 text-white' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                        }`}
                        data-testid={`upvote-${sug.suggestion_id}`}
                      >
                        <ChevronUp className="w-5 h-5" />
                      </button>
                      <span className={`text-sm font-bold ${
                        sug.upvotes - sug.downvotes > 0 ? 'text-indigo-600' :
                        sug.upvotes - sug.downvotes < 0 ? 'text-red-500' : 'text-gray-500'
                      }`} data-testid={`score-${sug.suggestion_id}`}>
                        {sug.upvotes - sug.downvotes}
                      </span>
                      <button
                        onClick={() => voteSuggestion(sug.suggestion_id, 'down')}
                        className={`p-1.5 rounded-lg transition-all ${
                          sug.user_vote === 'down' ? 'bg-red-500 text-white' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                        }`}
                        data-testid={`downvote-${sug.suggestion_id}`}
                      >
                        <ChevronDown className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${STATUS_COLORS[sug.status] || STATUS_COLORS.new}`}>
                          {sug.status}
                        </span>
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                          {sug.category}
                        </span>
                      </div>
                      <h3 className="font-bold text-gray-900 dark:text-white text-sm mb-1">{sug.title}</h3>
                      <p className="text-gray-600 dark:text-gray-400 text-sm mb-2">{sug.description}</p>
                      <div className="flex items-center gap-3 text-xs text-gray-400">
                        <div className="flex items-center gap-1">
                          <Avatar className="w-4 h-4">
                            <AvatarImage src={sug.author?.avatar_url} />
                            <AvatarFallback className="text-[8px]">{sug.author?.name?.charAt(0) || '?'}</AvatarFallback>
                          </Avatar>
                          <span>{sug.author?.name || 'Unknown'}</span>
                        </div>
                        <span>{formatTime(sug.created_at)}</span>
                        <button
                          onClick={() => toggleComments(sug.suggestion_id)}
                          className="flex items-center gap-1 hover:text-indigo-500 transition-colors"
                          data-testid={`comments-toggle-${sug.suggestion_id}`}
                        >
                          <MessageSquare className="w-3 h-3" />
                          {sug.comment_count || 0}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Comments Section */}
                  {expandedId === sug.suggestion_id && (
                    <div className="mt-4 pt-4 border-t dark:border-gray-700" data-testid={`comments-section-${sug.suggestion_id}`}>
                      <div className="flex gap-2 mb-3">
                        <input
                          type="text"
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && submitComment(sug.suggestion_id)}
                          placeholder="Add a comment..."
                          className="flex-1 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400"
                          data-testid={`comment-input-${sug.suggestion_id}`}
                        />
                        <button
                          onClick={() => submitComment(sug.suggestion_id)}
                          className="px-3 py-2 bg-indigo-500 text-white rounded-lg text-sm hover:bg-indigo-400"
                          data-testid={`comment-submit-${sug.suggestion_id}`}
                        >
                          <Send className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {(comments[sug.suggestion_id] || []).map(c => (
                          <div key={c.comment_id} className="flex gap-2 text-sm">
                            <Avatar className="w-6 h-6 flex-shrink-0">
                              <AvatarImage src={c.author?.avatar_url} />
                              <AvatarFallback className="text-[8px]">{c.author?.name?.charAt(0) || '?'}</AvatarFallback>
                            </Avatar>
                            <div>
                              <span className="font-semibold text-gray-800 dark:text-gray-200 text-xs">{c.author?.name}</span>
                              <span className="text-gray-400 text-xs ml-2">{formatTime(c.created_at)}</span>
                              <p className="text-gray-600 dark:text-gray-400 text-xs">{c.content}</p>
                            </div>
                          </div>
                        ))}
                        {(comments[sug.suggestion_id] || []).length === 0 && (
                          <p className="text-center text-gray-400 text-xs py-2">No comments yet</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            ))
          )}
        </div>
      </div>

      <FloatingDock user={user} />
    </div>
  );
};

export default SuggestionBox;
